import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiGet } from "../lib/api";
import { resolveApiUrl } from "../lib/url";
import { formatMassDateLabel, formatMassTimeParts } from "../lib/mass-time";
import { payloadTimeZone, zonedParts } from "../lib/timezone";
import { roleLabel } from "../lib/role-label";
import { resolveMspLoginUrl } from "../lib/msp-links";
import { supabase } from "../lib/supabaseClient";
import Spinner from "../components/Spinner";
import PoweredByFooter from "../components/PoweredByFooter";
import type { PublicSchedulePagePayload, PublicScheduleMass } from "../types/public-schedule";

// The parish's full published schedule at /s/<capability-slug>.
//
// Fully public: no login, no session, no parish provider — everything
// it renders comes from the one public endpoint, which resolves the
// parish from the slug alone. Replaces MSP's "Live Schedule Post",
// whose layout silently regressed between quarters into a 400-line
// single-column list with ministry in a right-hand column.
//
// This page must never be indexed. Three layers: the <meta> injected
// below, the X-Robots-Tag header the worker sets on the API response,
// and the /s/* rule in frontend/public/_headers that Pages applies to
// the HTML itself.
export default function PublicParishSchedule() {
  const { slug = "" } = useParams();
  const [data, setData] = useState<PublicSchedulePagePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  // This page is public and rendered outside every session provider, so
  // it doesn't otherwise know a signed-in volunteer arrived from their
  // dashboard. Read the Supabase session directly so the header button
  // becomes a way back to their own calendar instead of a Sign in that
  // would just loop them to the login page.
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    if (!supabase) return;
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) setSignedIn(Boolean(data.session));
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, sess) => {
      if (active) setSignedIn(Boolean(sess));
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Inject noindex for this route only, and remove it on unmount so it
  // can't leak onto the rest of the SPA via client-side navigation.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGet<PublicSchedulePagePayload>(`/public/schedule/${encodeURIComponent(slug)}`)
      .then((payload) => {
        if (cancelled) return;
        setData(payload);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError("This schedule link isn't valid, or it's been replaced with a new one.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (data?.parishName) document.title = `${data.parishName} — Mass schedule`;
  }, [data?.parishName]);

  // Name search: keeps a Mass when any volunteer under it matches, and
  // within that Mass keeps only the matching ministries/people, so
  // "find yourself" collapses the page to just your assignments rather
  // than merely highlighting them in place.
  // Partition on the FULL roster, BEFORE any search filtering.
  //
  // Doing this after filtering was a bug: the filter reduces each Mass
  // to only the people matching the query, so a 7-person Sunday 5:30
  // collapsed to "one ministry, one person" and tripped isDailyMass —
  // weekend Masses jumped into the Daily list the moment you searched a
  // name. Whether a Mass is a daily is a property of the Mass, not of
  // what you happened to type, so it has to be decided here.
  const { allWeekend, allDaily } = useMemo(() => {
    const w: PublicScheduleMass[] = [];
    const d: PublicScheduleMass[] = [];
    // Hooks run before the loading/error guards below, so data may
    // still be null on the first passes.
    for (const m of data?.masses ?? []) (isDailyMass(m) ? d : w).push(m);
    return { allWeekend: w, allDaily: d };
  }, [data?.masses]);

  // Parish timezone for every date shown on this page (fake-Z-aware
  // fallback for payloads served by a pre-PR-A worker or KV cache).
  const timeZone = payloadTimeZone(data?.timezone);

  const weekendGroups = useMemo(() => {
    const byWeekend = new Map<string, PublicScheduleMass[]>();
    for (const m of filterMasses(allWeekend, query)) {
      const k = weekendKey(m.startsAt, timeZone);
      byWeekend.set(k, [...(byWeekend.get(k) ?? []), m]);
    }
    return [...byWeekend.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [allWeekend, query, timeZone]);

  const dailies = useMemo(() => filterMasses(allDaily, query), [allDaily, query]);

  const weekendCount = weekendGroups.reduce((n, [, ms]) => n + ms.length, 0);
  const dailyCount = dailies.length;

  // Mobile-only tabs (CSS decides whether to honor `tab` — see
  // .pubsched__panel). Everything is one column under 760px, so
  // reaching the dailies meant scrolling past all 55 weekend cards.
  // Desktop still shows both sections, and print always emits both.
  const [tab, setTab] = useState<"weekend" | "daily">("weekend");

  // Find-yourself has to keep working with a tab hidden: if the search
  // has matches only in the other tab, go there rather than showing an
  // empty panel while the answer sits behind a tab they can't see.
  useEffect(() => {
    if (!query.trim()) return;
    if (tab === "weekend" && weekendCount === 0 && dailyCount > 0) setTab("daily");
    else if (tab === "daily" && dailyCount === 0 && weekendCount > 0) setTab("weekend");
  }, [query, tab, weekendCount, dailyCount]);

  // The one role shared by every daily Mass, if there is one. Read from
  // the UNFILTERED set: it describes the section, so a search that
  // happens to match a single role shouldn't relabel the heading.
  const uniformDailyRole = useMemo(() => {
    const roles = new Set(
      allDaily.flatMap((m) => m.ministries.map((g) => roleLabel(g.ministry))),
    );
    return roles.size === 1 ? [...roles][0]! : null;
  }, [allDaily]);

  if (loading) {
    return (
      <main className="page page--schedule">
        <Spinner label="Loading master calendar" />
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="page page--schedule">
        <div className="sign-in">
          <h1 className="sign-in__title">Schedule unavailable</h1>
          <p className="sign-in__lede">{error ?? "Something went wrong loading this schedule."}</p>
          <p className="sign-in__lede">Ask your parish coordinator for the current link.</p>
        </div>
      </main>
    );
  }

  const logoSrc = resolveApiUrl(data.parishLogoUrl);
  const countPeople = (ms: PublicScheduleMass[]) =>
    ms.reduce((n, m) => n + m.ministries.reduce((k, g) => k + g.volunteers.length, 0), 0);
  // Across both sections, so "3 assignments" still means what it says
  // when the matches are split between weekend and daily.
  const matchCount =
    countPeople(weekendGroups.flatMap(([, ms]) => ms)) + countPeople(dailies);

  return (
    <main className="page page--schedule">
      <header className="pubsched__head">
        {logoSrc ? (
          <img className="pubsched__logo" src={logoSrc} alt={`${data.parishName} logo`} />
        ) : null}
        <div className="pubsched__head-titles">
          <h1 className="pubsched__title">{data.parishName}</h1>
          <p className="pubsched__range">{formatRange(data.rangeStart, data.rangeEnd, timeZone)}</p>
        </div>
        {/* Top-right: signed-in visitors get a compact icon back to
            their own calendar (a text button was wide enough to shove
            the parish title); everyone else gets Sign in. Screen-only —
            a printed roster can't be clicked. */}
        {signedIn ? (
          <Link
            className="pubsched__mycal"
            to={`/${data.parishSlug}/dashboard`}
            aria-label="Your calendar"
            title="Your calendar"
          >
            <MyCalendarIcon />
          </Link>
        ) : (
          <a className="btn pubsched__signin-btn" href={`/${data.parishSlug}/login`}>
            Sign in
          </a>
        )}
      </header>

      {/* The confusion this page exists to prevent: the coordinator
          feared the regressed MSP post had broken sub-requests for the
          whole parish. Say plainly that swaps don't happen here — and
          hand over the actual link rather than describing where to go.
          The printed version exposes the full URL (see @media print).*/}
      <aside className="pubsched__notice">
        <p>
          <strong>Need a substitute?</strong> Sub-requests aren't made on this page — you request
          one in Ministry Scheduler Pro, under the <strong>My Schedule</strong> tab.
        </p>
        <p className="pubsched__notice-actions">
          <a
            className="pubsched__notice-link"
            href={resolveMspLoginUrl(data.mspLoginUrl)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Go to Ministry Scheduler Pro →
          </a>
        </p>
      </aside>

      {/* Shown only under 760px (CSS). Above the search because the tab
          is the coarser choice — pick your section, then filter within
          it — and because sticking it to the top of a long schedule is
          only useful if it sits above the scroll. */}
      {data.masses.length > 0 ? (
        <div className="pubsched__tabs" role="tablist" aria-label="Schedule sections">
          <button
            type="button"
            role="tab"
            id="tab-weekend"
            aria-selected={tab === "weekend"}
            aria-controls="panel-weekend"
            className={`pubsched__tab${tab === "weekend" ? " is-active" : ""}`}
            onClick={() => setTab("weekend")}
          >
            Weekend Masses
            <span className="pubsched__tab-count">{weekendCount}</span>
          </button>
          <button
            type="button"
            role="tab"
            id="tab-daily"
            aria-selected={tab === "daily"}
            aria-controls="panel-daily"
            className={`pubsched__tab${tab === "daily" ? " is-active" : ""}`}
            onClick={() => setTab("daily")}
          >
            Daily Masses
            <span className="pubsched__tab-count">{dailyCount}</span>
          </button>
        </div>
      ) : null}

      <div className="pubsched__tools">
        {/* Label and freshness stamp share a line, stamp pushed right —
            it fills otherwise-empty space instead of costing its own
            row, and stays subordinate to the search. Split from the
            <input> (rather than wrapping it in a <label>) so the two
            can sit on one flex row; htmlFor keeps them associated. */}
        <div className="pubsched__tools-head">
          <label className="sign-in__label" htmlFor="pubsched-search">
            Find your name
          </label>
          <span className="pubsched__updated">Updated {formatUpdated(data.fetchedAt)}</span>
        </div>
        <input
          id="pubsched-search"
          type="search"
          className="sign-in__input pubsched__search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Start typing a name…"
          autoComplete="off"
        />
      </div>

      {query.trim() ? (
        <p className="pubsched__result-count">
          {matchCount === 0
            ? `No one matching “${query.trim()}” is on this schedule.`
            : `${matchCount} assignment${matchCount === 1 ? "" : "s"} for “${query.trim()}”.`}
        </p>
      ) : null}

      {data.masses.length === 0 ? (
        <section className="panel">
          <p className="panel__empty">No Masses are published for this period yet.</p>
        </section>
      ) : (
        <>
          <div
            id="panel-weekend"
            role="tabpanel"
            aria-labelledby="tab-weekend"
            className={`pubsched__panel${tab === "weekend" ? "" : " is-inactive"}`}
          >
            {weekendCount === 0 && query.trim() ? (
              <p className="pubsched__result-count">No weekend Masses match that name.</p>
            ) : null}
            {weekendGroups.map(([key, masses]) => (
            <section className="pubsched__weekend" key={key}>
              <h2 className="pubsched__weekend-title">{groupLabel(masses, timeZone)}</h2>
              <div className="pubsched__masses">
                {masses.map((mass) => (
                  <MassBlock
                    key={`${mass.startsAt}-${mass.label}`}
                    mass={mass}
                    query={query.trim()}
                    timeZone={timeZone}
                  />
                ))}
              </div>
            </section>
            ))}
          </div>

          <div
            id="panel-daily"
            role="tabpanel"
            aria-labelledby="tab-daily"
            className={`pubsched__panel${tab === "daily" ? "" : " is-inactive"}`}
          >
            {dailies.length > 0 ? (
            <section className="pubsched__weekend pubsched__daily">
              <h2 className="pubsched__weekend-title">
                Daily Masses
                {/* At OLG every daily Mass is a Weekday Lector, so
                    stamping the role on all 39 rows is just noise —
                    say it once here and drop it from the rows. Falls
                    back to per-row labels if a parish ever mixes
                    roles across its weekday Masses. */}
                {uniformDailyRole ? (
                  <span className="pubsched__weekend-note">{uniformDailyRole}</span>
                ) : null}
              </h2>
              <ul className="pubsched__daily-list">
                {dailies.map((mass) => (
                  <DailyRow
                    key={`${mass.startsAt}-${mass.label}`}
                    mass={mass}
                    query={query.trim()}
                    showRole={!uniformDailyRole}
                    timeZone={timeZone}
                  />
                ))}
              </ul>
            </section>
            ) : (
              <p className="pubsched__result-count">
                {query.trim()
                  ? "No daily Masses match that name."
                  : "No daily Masses are published for this period."}
              </p>
            )}
          </div>
        </>
      )}

      <PoweredByFooter />
    </main>
  );
}

function MassBlock({
  mass,
  query,
  timeZone,
}: {
  mass: PublicScheduleMass;
  query: string;
  timeZone: string;
}) {
  const d = new Date(mass.startsAt);
  const { time, suffix } = formatMassTimeParts(d, timeZone);
  return (
    <section className="pubsched__mass">
      <h2 className="pubsched__mass-head">
        <span className="pubsched__mass-date">{formatMassDateLabel(d, timeZone)}</span>
        <span className="pubsched__mass-time">
          {time}
          <span className="pubsched__mass-time-suffix">{suffix}</span>
        </span>
      </h2>
      <div className="pubsched__ministries">
        {mass.ministries.map((g) => (
          <div className="pubsched__ministry" key={g.ministry}>
            <h3 className="pubsched__ministry-name">{roleLabel(g.ministry)}</h3>
            <ul className="pubsched__names">
              {g.volunteers.map((name) => (
                <li className="pubsched__name" key={name}>
                  {query ? highlight(name, query) : name}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

// One line per daily Mass: when on the left, who on the right. These
// are one-person Masses, so a full ministry-grouped card would be
// mostly chrome — this packs 39 of them into a few columns instead of
// 39 cards.
function DailyRow({
  mass,
  query,
  showRole,
  timeZone,
}: {
  mass: PublicScheduleMass;
  query: string;
  showRole: boolean;
  timeZone: string;
}) {
  const d = new Date(mass.startsAt);
  const when = d.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone,
  });
  const { time, suffix } = formatMassTimeParts(d, timeZone);
  const ampm = suffix.replace(" Mass", "");
  // Flattened: by definition at most one ministry and two names here.
  const people = mass.ministries.flatMap((g) =>
    g.volunteers.map((name) => ({ name, ministry: g.ministry })),
  );
  return (
    <li className="pubsched__daily-row">
      <span className="pubsched__daily-when">
        {when} · {time} {ampm}
      </span>
      <span className="pubsched__daily-who">
        {people.map((p, i) => (
          <span key={`${p.name}-${i}`}>
            {i > 0 ? ", " : ""}
            {query ? highlight(p.name, query) : p.name}
          </span>
        ))}
        {showRole && people[0] ? (
          <span className="pubsched__daily-role">{roleLabel(people[0].ministry)}</span>
        ) : null}
      </span>
    </li>
  );
}

// Wraps the matched substring so a found name is obvious at a glance
// even after filtering.
function highlight(name: string, query: string) {
  const i = name.toLowerCase().indexOf(query.toLowerCase());
  if (i === -1) return name;
  return (
    <>
      {name.slice(0, i)}
      <mark className="pubsched__mark">{name.slice(i, i + query.length)}</mark>
      {name.slice(i + query.length)}
    </>
  );
}

// Narrows each Mass to just the people matching the query, dropping
// Masses with no match. Applied per-section AFTER the daily/weekend
// split — never before it, or the narrowed roster changes what a Mass
// looks like to isDailyMass.
function filterMasses(masses: PublicScheduleMass[], query: string): PublicScheduleMass[] {
  const q = query.trim().toLowerCase();
  if (!q) return masses;
  const out: PublicScheduleMass[] = [];
  for (const mass of masses) {
    const ministries = mass.ministries
      .map((g) => ({ ...g, volunteers: g.volunteers.filter((n) => n.toLowerCase().includes(q)) }))
      .filter((g) => g.volunteers.length > 0);
    if (ministries.length > 0) out.push({ ...mass, ministries });
  }
  return out;
}

// A "daily" Mass: one ministry, at most two people. At OLG that's the
// 8:30am weekday Mass with a single Weekday Lector — 39 of 94 Masses,
// i.e. 41% of the cards but ~4% of the names. Giving each one a full
// card buried the weekend Masses (7–16 people apiece) that are the
// actual content, so these get a compact list instead.
//
// Deliberately keyed on roster SHAPE, not day-of-week: OLG's Saturday
// 8:30am is a daily Mass while Saturday 5:30pm is the vigil, so a
// weekday/weekend calendar split would misclassify it. Shape also
// degrades sensibly for any parish whose ministries are named
// differently.
function isDailyMass(m: PublicScheduleMass): boolean {
  const people = m.ministries.reduce((n, g) => n + g.volunteers.length, 0);
  return m.ministries.length <= 1 && people <= 2;
}

// Saturday's vigil belongs with the following day's Masses — people
// think in weekends, not calendar days. Weekday and day key read in
// the parish timezone (startsAt is a real UTC instant since PR A).
// Saturday advances to Sunday by calendar-day arithmetic (Date.UTC
// handles month rollover), which no DST hour-shift can perturb.
function weekendKey(startsAt: string, timeZone: string): string {
  const p = zonedParts(new Date(startsAt), timeZone);
  const d = new Date(Date.UTC(p.year, p.month - 1, p.day + (p.weekday === 6 ? 1 : 0)));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

function groupLabel(masses: PublicScheduleMass[], timeZone: string): string {
  const ds = masses
    .map((m) => new Date(m.startsAt))
    .sort((a, b) => a.getTime() - b.getTime());
  const first = ds[0]!;
  const last = ds[ds.length - 1]!;
  const mon = (d: Date) => d.toLocaleString("en-US", { month: "long", timeZone });
  const day = (d: Date) => d.toLocaleString("en-US", { day: "numeric", timeZone });
  const span =
    day(first) === day(last)
      ? `${mon(first)} ${day(first)}`
      : mon(first) === mon(last)
        ? `${mon(first)} ${day(first)}–${day(last)}`
        : `${mon(first)} ${day(first)} – ${mon(last)} ${day(last)}`;
  // Only call it a weekend when it actually is one — a big-roster
  // weekday Mass (Holy Week, a funeral) would land here too.
  const isWeekend = ds.some((d) => {
    const { weekday } = zonedParts(d, timeZone);
    return weekday === 0 || weekday === 6;
  });
  return isWeekend ? `Weekend of ${span}` : span;
}

function formatRange(start: string | null, end: string | null, timeZone: string): string {
  if (!start || !end) return "Mass schedule";
  const a = new Date(start);
  const b = new Date(end);
  const fmt = (d: Date) =>
    d.toLocaleString("en-US", { month: "long", day: "numeric", timeZone });
  const year = b.toLocaleString("en-US", { year: "numeric", timeZone });
  return `${fmt(a)} – ${fmt(b)}, ${year}`;
}

function formatUpdated(iso: string): string {
  const d = new Date(iso);
  // fetchedAt is a real UTC instant (unlike MSP's wall-clock times), so
  // render it in the reader's own timezone.
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// "Your calendar" glyph — a calendar with a person on its face,
// distinguishing this personal-schedule link from the plain calendar
// icon in the volunteer header. Sized/coloured by .pubsched__mycal.
function MyCalendarIcon() {
  return (
    <svg viewBox="0 0 16 16" width="20" height="20" aria-hidden="true">
      <rect x="1.5" y="2.6" width="13" height="11.9" rx="1.6" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <line x1="1.5" y1="5.6" x2="14.5" y2="5.6" stroke="currentColor" strokeWidth="1.3" />
      <line x1="4.6" y1="1.4" x2="4.6" y2="3.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <line x1="11.4" y1="1.4" x2="11.4" y2="3.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      {/* person on the calendar face */}
      <circle cx="8" cy="8.7" r="1.55" fill="currentColor" />
      <path d="M4.9 13.4 a3.1 3.1 0 0 1 6.2 0 Z" fill="currentColor" />
    </svg>
  );
}
